import { Injectable } from '@angular/core';
import { AppState } from '../models/AppState';
import { OrderItem } from '../models/Order';
import { WhenFilter } from '../models/FSMenu';
import { MenuUtils } from '../utils/Utils';
import { FilterLogic, Filter, ICParentLookup } from '../utils/filter-logic';
import { parseWhenFilterString } from '../utils/when-filter-parser';
import moment from 'moment';
import {ActiveTipConfig} from '../models/active-tip-config-model';

@Injectable({
  providedIn: 'root'
})
export class TipService {

  constructor() {}

  getTip(appState: AppState, items: OrderItem[], interfaceType: string, forceNoTip: boolean): ActiveTipConfig | null {
    if (forceNoTip) {
      return this.parseTipMode('OFF', false);
    }

    const tipLevels = appState.cfg.tip?.tip_levels;

    if (!tipLevels || tipLevels.length === 0) {
      return null;
    }

    //console.log("Evaluating tip levels:", tipLevels);

    const now = moment();

    for (const tipConfig of tipLevels) {
      // Check if active
      if (tipConfig.active != true) {
        continue;
      }

      // Check interface type
      if (tipConfig.interface !== interfaceType) {
        continue;
      }

      // Check when filter
      if (tipConfig.when) {
        let whenFilter: WhenFilter | null = null;

        // Try parsing as JSON first
        try {
          whenFilter = JSON.parse(tipConfig.when) as WhenFilter;
        } catch (e) {
          // If JSON parse fails, try parsing as string format
          whenFilter = parseWhenFilterString(tipConfig.when);
        }

        if (whenFilter) {
          if (!MenuUtils.isWhenAtDate(whenFilter, now)) {
            continue;
          }
        } else {
          console.error('Failed to parse when filter:', tipConfig.when);
          continue;
        }
      }
      // No when filter means always match

      // Check item filter
      if (tipConfig.filter) {
        try {
          const filterExpressions = FilterLogic.parseFilterExpressions(tipConfig.filter);
          const filter = new Filter(filterExpressions, appState.order.user_cfg, new ICParentLookup(appState.cfg?.ic));

          // Check if any item matches the filter
          // We need to find the MenuItem from the menu structure for each OrderItem
          const hasMatchingItem = items.some(orderItem => {
            const menuItem = MenuUtils.getItemInMenu(orderItem.item, appState.menu);
            if (menuItem) {
              return filter.itemMatches(menuItem);
            }
            return false;
          });

          if (!hasMatchingItem) {
            continue;
          }
        } catch (e) {
          console.error('Failed to parse filter:', tipConfig.filter, e);
          continue;
        }
      }
      // No filter means always match

      console.log("Matching tip config:", tipConfig);

      // Found a match, convert tip_mode to TipResult
      const tr = this.parseTipMode(tipConfig.tip_mode, tipConfig.force_selection==true);
      return tr;
    }

    return null;
  }

  private parseTipMode(tipMode: string, forceSelection: boolean): ActiveTipConfig|null {

    if (tipMode === 'OFF') {
      return {
        tipActive: false,
        tipLevel1: 0,
        tipLevel2: 0,
        tipLevel3: 0,
        defaultTip: 0,
        defaultButtonId: "0"
      };
    }

    const parts = tipMode.split('-').map(p => parseInt(p, 10));

    const defaultTip = forceSelection ? undefined : 0;
    const tipButtonId = forceSelection ? "not_selected" : "0";

    if (parts.length === 3) {

      return {
        tipActive: true,
        tipLevel1: parts[0],
        tipLevel2: parts[1],
        tipLevel3: parts[2],
        defaultTip: defaultTip,
        defaultButtonId: tipButtonId
      };
    }

    return null;
  }

  // Legacy method to get tip config when the new tip configuration is missing
  getTipConfigWhenMissingConfig(forceNoTip: boolean, use_tip: boolean | undefined): ActiveTipConfig {
    let tipActive = true;
    if (forceNoTip) {
      // If receipt is blocking from using tips, disable tips
      tipActive = false;
    } else {
      // Else check if tips are enabled at the venue
      tipActive = use_tip == true;
    }

    return {
      tipActive: tipActive,
      tipLevel1: 5,
      tipLevel2: 10,
      tipLevel3: 15,
      defaultTip: 0,
      defaultButtonId: "0"
    };
  }
}
